import { Card } from "components/Card";
import { BaseStrategy, StrategyOptions } from "./BaseStrategy";
import { DropCardPlace, GamePlace, KING_VALUE } from "constants/cards";
import { FOUNDATION_Z_INDEX } from "constants/layout";
import { getUniqueItems } from "game/helpers/array";
import { moveSound } from "game/foundationStreakManager";
import { GameEvent } from "game/events/gameEvent";
import { EventName } from "game/events/eventsName";
import { SoundManager } from "game/core/soundManager";
import { canRemoveKing, canRemovePyramidCardPair, refreshPyramidCards, refreshZIndexes } from "game/utils/pyramid";
import { areBoxesCollide } from "game/helpers/collision";
import { PyramidView } from "components/PyramidView";
import { Layout } from "game/LayoutManager";
import { showPopup } from "game/showPopup";
import { GAEvent, GAEventNames } from "game/analytics/events";
import { Store } from "game/store/gameStore";
import gsap from 'gsap';

// Constants
const ANIMATION_CONSTANTS = {
    CARD_ARC_HEIGHT: -3.5,
    CARD_SPREAD_DISTANCE: 2.8,
    ANIMATION_DURATION: 0.9,
    ROTATION_DURATION: 0.7,
    ROTATION_MULTIPLIER: 4,
    BEZIER_POINTS: 20,
    HIGH_Z_INDEX: 9999
} as const;

const SOUND_DELAYS = {
    CLICK: 150,
    DRAG: 50
} as const;

export class PyramidStrategy extends BaseStrategy {
    constructor(options: StrategyOptions) {
        super(options);
    }

    canDropCardsOnFirstAction(lastCard: Card | null): boolean {
        return !lastCard && this.game.moveGroup[0].value === KING_VALUE;
    }

    forEachCard(action: (card: Card) => void): void {
        for (let row = 0; row < PyramidView.pyramidRows; row++) {
            const column = this.game.columns[row];
            for (let col = 0; col <= row; col++) {
                const card = column.cards[col];
                if (card) action(card);
            }
        }
    }

    handleRemoveCarsFromTable(targetCard: Card | null, cards: (Card|null)[]): void {
        if (!targetCard) return;
        
        const index = cards.findIndex(c => c?._id === targetCard._id);
        if (index !== -1) cards[index] = null;
    }

    noHintHandler(): void {
        showPopup('no-hints');
        GAEvent(GAEventNames.SHOW_NO_HINT_MODAL, {
            moves: Store.movesCount,
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
    }

    handleInsertCardInToTable(cards: Card[], card: Card): void {
        cards.splice(card.place.order, 1, card);
    }

    refreshBoardState(): void {
        refreshPyramidCards(this.game);
        refreshZIndexes(this.game);
    }

    checkWinCondition(): boolean {
        let pyramidCardsRemaining = 0;
        
        for (const column of this.game.columns) {
            pyramidCardsRemaining += column.cards.filter(card => 
                card && this.game.cards.includes(card)
            ).length;
        }
        
        return pyramidCardsRemaining === 0;
    }

    private logCardOperation(operation: string, card: Card, details?: string): void {
        const info = details ? ` - ${details}` : '';
        console.log(`[PYRAMID ${operation}] ${card.code}(${card.value})${info}`);
    }

    private logMoveGroup(operation: string, moveGroup: Card[]): void {
        console.log(`[PYRAMID ${operation}] ${moveGroup.length} cards:`, 
            moveGroup.map(c => `${c.code}(${c.value})`));
    }

    private createCardAnimation(card: Card, index: number, foundation: any): gsap.core.Tween {
        const { height } = card.container;
        const dx = (index + 1) % 2 === 0 ? -height * ANIMATION_CONSTANTS.CARD_SPREAD_DISTANCE : height * ANIMATION_CONSTANTS.CARD_SPREAD_DISTANCE;
        const dy = foundation.container.y - card.container.y;
        const arcHeight = height * ANIMATION_CONSTANTS.CARD_ARC_HEIGHT;
        
        card.container.zIndex = ANIMATION_CONSTANTS.HIGH_Z_INDEX;

        // Create bezier curve points
        const points = this.createBezierPoints(
            card.container.x, card.container.y, 
            dx, dy, arcHeight
        );

        // Rotation animation
        const rotation = card.container.rotation + ANIMATION_CONSTANTS.ROTATION_MULTIPLIER * Math.PI;
        const startRotation = card.container.rotation;
        
        gsap.to(card.container, {
            rotation: (index + 1) % 2 === 0 ? -rotation : rotation,
            duration: ANIMATION_CONSTANTS.ROTATION_DURATION,
            onComplete: () => {
                card.container.rotation = startRotation;
            }
        });
        
        return gsap.to(card.container.position, {
            duration: ANIMATION_CONSTANTS.ANIMATION_DURATION,
            motionPath: { path: points, autoRotate: false }
        });
    }

    private createBezierPoints(startX: number, startY: number, dx: number, dy: number, arcHeight: number): { x: number; y: number }[] {
        const points: { x: number; y: number }[] = [];
        const apexX = startX + dx;
        const apexY = startY + arcHeight;
        
        for (let i = 0; i < ANIMATION_CONSTANTS.BEZIER_POINTS; i++) {
            const t = i / (ANIMATION_CONSTANTS.BEZIER_POINTS - 1);
            const x = (1 - t) ** 2 * startX + 2 * (1 - t) * t * apexX + t ** 2 * (startX + dx);
            const y = (1 - t) ** 2 * startY + 2 * (1 - t) * t * apexY + t ** 2 * (startY + dy);
            points.push({ x, y });
        }
        
        return points;
    }

    private setupCardForFoundation(card: Card, foundation: any): void {
        foundation.addCard(card);
        card.container.zIndex += 100;
        card.setPlace({
            cardPlace: GamePlace.FOUNDATION,
            value: 0,
            zIndex: foundation.length + FOUNDATION_Z_INDEX,
            position: Layout.getFoundationPosition(0),
            order: 0,
        });
    }

    private processSuccessfulDrop(moveGroup: Card[], autoPlay: boolean): void {
        const firstCard = moveGroup[0];
        const foundation = this.game.foundations[0];
        
        this.logCardOperation('DROP', firstCard, 'Valid drop to foundation');
        this.logMoveGroup('DROP', moveGroup);
        
        this.game.moves.add(moveGroup, false);
        GameEvent.emit(EventName.ADD_SCORE, { from: firstCard.place.cardPlace, to: GamePlace.FOUNDATION });
        moveSound(GamePlace.FOUNDATION, { autocomplete: this.game.autocompleteActivated, autoPlay });
        
        // Remove cards from board
        moveGroup.forEach(card => {
            this.logCardOperation('REMOVE', card, `from ${card.place.cardPlace}`);
            card.resetHighlight();
            this.game.removeCardFromBoard(card);
        });
        
        // Refresh waste pile if needed
        if (this.game.wastePile && moveGroup.some(card => card.place.cardPlace === GamePlace.WASTE_PILE)) {
            this.game.wastePile.refresh();
        }
        
        // Setup cards for animation
        if (moveGroup.length > 1) {
            moveGroup[0].container.position = moveGroup[1].container.position.clone();
            moveGroup[0].container.zIndex = moveGroup[1].container.zIndex;
        }

        // Create animations
        const animations = moveGroup.map((card, index) => {
            this.setupCardForFoundation(card, foundation);
            return this.createCardAnimation(card, index, foundation);
        });

        // Handle post-animation cleanup
        GameEvent.emit(EventName.SET_UNDO_DISABLED, true);
        this.refreshBoardState();
        this.handleChangeHighlight();
        
        const currentCards = [...moveGroup];
        this.game.placeMoveGroup(true, autoPlay, true, true);
        
        Promise.all(animations).then(() => {
            GameEvent.emit(EventName.SET_UNDO_DISABLED, false);
            currentCards.forEach((card, index) => {
                const lastMove = index === moveGroup.length - 1;
                this.game.moveCardIntoPlace(card, index, 0, true, autoPlay, lastMove, true);
            });
        });
    }

    private processFailedDrop(moveGroup: Card[], isClick: boolean): void {
        this.logMoveGroup('DROP', moveGroup);
        console.log('[PYRAMID DROP] Invalid drop - cards cannot be removed');
        
        moveGroup.forEach(card => card.shakeCard());
        SoundManager.play('missCardSFX', { delay: isClick ? SOUND_DELAYS.CLICK : SOUND_DELAYS.DRAG });
        this.game.placeMoveGroup(false);
    }

    handleDropCards(moveGroup: Card[], autoPlay: boolean, isClick: boolean): void {
        const foundation = this.game.foundations[0];
        
        if (foundation.canDropCard(moveGroup)) {
            this.processSuccessfulDrop(moveGroup, autoPlay);
        } else {
            this.processFailedDrop(moveGroup, isClick);
        }
    }

    private findCollisionPair(firstCard: Card): { found: boolean; pairCard: Card | null } {
        for (const column of this.game.columns) {
            for (const targetCard of column.cards) {
                if (!targetCard || targetCard === firstCard || !targetCard.active || !targetCard.visible) continue;
                
                if (areBoxesCollide(firstCard.container, targetCard.container)) {
                    return {
                        found: canRemovePyramidCardPair(firstCard, targetCard),
                        pairCard: targetCard
                    };
                }
            }
        }
        return { found: false, pairCard: null };
    }

    private handleDragCollision(moveGroup: Card[], isAutoPlay: boolean): boolean {
        const firstCard = moveGroup[0];
        const { found, pairCard } = this.findCollisionPair(firstCard);
        
        if (found && pairCard) {
            moveGroup.push(pairCard);
            moveGroup.forEach(card => card.cardDisplay.container.position.set(0, 0));
            this.handleDropCards(moveGroup, isAutoPlay, false);
            return true;
        }
        
        // Handle failed collision
        if (pairCard) pairCard.shakeCard();
        moveGroup.forEach(card => card.resetHighlight());
        SoundManager.play('missCardSFX', { delay: SOUND_DELAYS.DRAG });
        moveGroup.forEach(card => card.cardDisplay.container.position.set(0, 0));
        this.game.moveGroup = [];
        return true;
    }

    handleCheckMoveGroupForPair(moveGroup: Card[], isClick: boolean, isAutoPlay: boolean = false): void {
        moveGroup.forEach(card => card.container.zIndex = card.place.zIndex);
        
        if (!isClick && moveGroup.length === 1) {
            if (this.handleDragCollision(moveGroup, isAutoPlay)) return;
        }
        
        if (isClick) {
            moveGroup.forEach(card => card.cardDisplay.container.position.set(0, 0));
            this.handleCardClick(moveGroup, true);
            return;
        }

        this.handleDropCards(moveGroup, isAutoPlay, isClick);
    }

    onDragCard(moveGroup: Card[]): boolean {
        this.game.onExternalClick();

        const { uniqueItems } = getUniqueItems(moveGroup, (item, index, self) => 
            index === self.findIndex(obj => obj.code === item.code)
        );
        
        this.game.moveGroup = uniqueItems;

        if (uniqueItems.length >= 2) {
            uniqueItems.forEach(card => card.resetHighlight());
            this.handleDropCards(uniqueItems, false, false);
            return true;
        }

        return false;
    }

    onCreateMoveGroup(currentMoveGroup: Card[], card: Card): void {
        super.onCreateMoveGroup(currentMoveGroup, card);

        if (card.place.cardPlace === GamePlace.TABLEAU) {
            this.game.moveGroup.push(card);
            this.logCardOperation('MOVE', card, 'Added pyramid card to move group');
        } else if (card.place.cardPlace === GamePlace.WASTE_PILE) {
            const dragCard = this.game.wastePile?.lastCard;
            if (dragCard) {
                this.game.moveGroup.push(dragCard);
                this.logCardOperation('MOVE', dragCard, 'Added waste pile card to move group');
            }
        }
    }

    private handleCardSelection(moveGroup: Card[], lastCard: Card, isClick: boolean): boolean {
        if (isClick && lastCard.isHighlighted) {
            this.logCardOperation('CLICK', lastCard, 'Card already in move group - removing');
            this.game.moveGroup = moveGroup.filter(c => c !== lastCard);
            lastCard.resetHighlight();
            return true;
        }
        
        lastCard.highlight();
        return false;
    }

    private processPairSelection(moveGroup: Card[], isClick: boolean): boolean {
        const [firstCard, secondCard] = moveGroup;
        
        if (firstCard && secondCard) {
            if (canRemovePyramidCardPair(firstCard, secondCard)) {
                this.logCardOperation('CLICK', firstCard, `Valid pair with ${secondCard.code}`);
                this.handleDropCards(moveGroup, false, isClick);
                return true;
            } else {
                this.logCardOperation('CLICK', firstCard, `Invalid pair with ${secondCard.code}`);
                moveGroup.forEach(card => card.resetHighlight());
                this.game.moveGroup = [];
                return true;
            }
        }
        
        if (canRemoveKing(firstCard)) {
            this.logCardOperation('CLICK', firstCard, 'King - dropping immediately');
            moveGroup.forEach(card => card.resetHighlight());
            this.handleDropCards(moveGroup, false, isClick);
            return true;
        }
        
        return false;
    }

    handleCardClick(moveGroup: Card[], isClick: boolean): void {
        const lastCard = moveGroup[moveGroup.length - 1];
        if (!lastCard) return;

        this.logCardOperation('CLICK', lastCard, 
            `active: ${lastCard.active}, visible: ${lastCard.visible}, place: ${lastCard.place.cardPlace}`);

        if (this.handleCardSelection(moveGroup, lastCard, isClick)) return;
        
        if (moveGroup.length > 0) {
            if (this.processPairSelection(moveGroup, isClick)) return;
            console.log('[PYRAMID CLICK] Waiting for second card to complete pair');
        }
    }

    getAvailableClickPlace(): DropCardPlace[] {
        return this.game.foundations.map((_, index) => ({
            place: GamePlace.FOUNDATION,
            dropColumnIndex: index
        }));
    }
}